import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTRPC } from '@/trpc/react'
import { arktypeResolver } from '@hookform/resolvers/arktype'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type } from 'arktype'
import { PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/soa_government_contributions/')({
  component: RouteComponent,
})

const formSchema = type({
  rangeStart: 'string',
  rangeEnd: 'string',
  sss: 'string',
  phic: 'string',
  hdmf: 'string',
})

type FormValues = typeof formSchema.infer

interface SoaBracket {
  bracketId: string
  rangeStart: string
  rangeEnd: string
  sss: string
  phic: string
  hdmf: string
}

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingBracket, setEditingBracket] = useState<SoaBracket | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const { data: brackets, isPending } = useQuery({
    ...api.soaGovernmentContributions.getSoaGovernmentContributionBrackets.queryOptions(),
  })

  const { mutate: createBracket } = useMutation({
    ...api.soaGovernmentContributions.createSoaGovernmentContributionBracket.mutationOptions(),
  })

  const { mutate: editBracket } = useMutation({
    ...api.soaGovernmentContributions.editSoaGovernmentContributionBracket.mutationOptions(),
  })

  const { mutate: deleteBracket } = useMutation({
    ...api.soaGovernmentContributions.deleteSoaGovernmentContributionBracket.mutationOptions(),
  })

  const addForm = useForm<FormValues>({
    resolver: arktypeResolver(formSchema),
    defaultValues: {
      rangeStart: '',
      rangeEnd: '',
      sss: '',
      phic: '',
      hdmf: '',
    },
  })

  const editForm = useForm<FormValues>({
    resolver: arktypeResolver(formSchema),
    defaultValues: {
      rangeStart: '',
      rangeEnd: '',
      sss: '',
      phic: '',
      hdmf: '',
    },
  })

  function handleAdd(data: FormValues) {
    createBracket(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: api.soaGovernmentContributions.getSoaGovernmentContributionBrackets.queryKey(),
        })
        toast.success('Government contribution bracket added successfully')
        setAddDialogOpen(false)
        addForm.reset()
      },
      onError: (error) => {
        toast.error('Failed to add government contribution bracket')
        console.error(error)
      },
    })
  }

  function handleEditOpen(bracket: SoaBracket) {
    setEditingBracket(bracket)
    editForm.reset({
      rangeStart: bracket.rangeStart,
      rangeEnd: bracket.rangeEnd,
      sss: bracket.sss,
      phic: bracket.phic,
      hdmf: bracket.hdmf,
    })
    setEditDialogOpen(true)
  }

  function handleEdit(data: FormValues) {
    if (!editingBracket) return

    editBracket(
      { ...data, bracketId: editingBracket.bracketId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey:
              api.soaGovernmentContributions.getSoaGovernmentContributionBrackets.queryKey(),
          })
          toast.success('Government contribution bracket updated successfully')
          setEditDialogOpen(false)
          setEditingBracket(null)
          editForm.reset()
        },
        onError: (error) => {
          toast.error('Failed to update government contribution bracket')
          console.error(error)
        },
      },
    )
  }

  function handleDelete() {
    if (!deleteId) return

    deleteBracket(
      { bracketId: deleteId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey:
              api.soaGovernmentContributions.getSoaGovernmentContributionBrackets.queryKey(),
          })
          toast.success('Government contribution bracket deleted successfully')
          setDeleteId(null)
        },
        onError: (error) => {
          toast.error('Failed to delete government contribution bracket')
          console.error(error)
        },
      },
    )
  }

  function formatCurrency(value: string) {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
    }).format(parseFloat(value))
  }

  return (
    <DashboardShell>
      <DashboardHeader heading="Statement of Account" text="Government Contributions (Monthly)">
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <PlusIcon className="mr-2 h-4 w-4" />
              Add New Bracket
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Bracket</DialogTitle>
            </DialogHeader>
            <Form {...addForm}>
              <form onSubmit={addForm.handleSubmit(handleAdd)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={addForm.control}
                    name="rangeStart"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Range Start (₱)</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={addForm.control}
                    name="rangeEnd"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Range End (₱)</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={addForm.control}
                    name="sss"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SSS</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={addForm.control}
                    name="phic"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>PHIC</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={addForm.control}
                    name="hdmf"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>HDMF</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={addForm.formState.isSubmitting}>
                    {addForm.formState.isSubmitting ? 'Adding...' : 'Add Bracket'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </DashboardHeader>

      <Card>
        <CardHeader>
          <CardTitle>Government Contributions (Monthly)</CardTitle>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <div>Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Range Start (₱)</TableHead>
                  <TableHead>Range End (₱)</TableHead>
                  <TableHead>SSS</TableHead>
                  <TableHead>PHIC</TableHead>
                  <TableHead>HDMF</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brackets?.map((bracket) => (
                  <TableRow key={bracket.bracketId}>
                    <TableCell>{formatCurrency(bracket.rangeStart)}</TableCell>
                    <TableCell>{formatCurrency(bracket.rangeEnd)}</TableCell>
                    <TableCell>{formatCurrency(bracket.sss)}</TableCell>
                    <TableCell>{formatCurrency(bracket.phic)}</TableCell>
                    <TableCell>{formatCurrency(bracket.hdmf)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditOpen(bracket)}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteId(bracket.bracketId)}
                        >
                          <Trash2Icon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )) || (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center">
                      No brackets found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Bracket</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit(handleEdit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="rangeStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Range Start (₱)</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="rangeEnd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Range End (₱)</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={editForm.control}
                  name="sss"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SSS</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="phic"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>PHIC</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="hdmf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>HDMF</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={editForm.formState.isSubmitting}>
                  {editForm.formState.isSubmitting ? 'Updating...' : 'Update Bracket'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bracket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this government contribution bracket? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  )
}
